import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router'

import { LazyLoadingMainComponent, LazyPageComponent, LoadingRoute } from '../components/LazyComponent'

import { useSession } from '../providers/SessionProvider'

const Pulse = React.lazy(() => import('../containers/Pulse'))

const CruxDashboard = React.lazy(() => import('../containers/Crux/Dashboard'))
const CruxPagesOverview = React.lazy(() => import('../containers/Crux/Pages'))

const BudgetsOverview = React.lazy(() => import('../components/templates/Budgets/Overview'))
const Budget = React.lazy(() => import('../components/templates/Budgets/Budget'))
const NewBudget = React.lazy(() => import('../components/templates/Budgets/New'))
const EditBudget = React.lazy(() => import('../components/templates/Budgets/Edit'))

const Pages = React.lazy(() => import('../components/templates/Pages/Pages'))

const Metric = React.lazy(() => import('../containers/Metric'))

const Snapshots = React.lazy(() => import('../containers/Snapshots'))

const Snapshot = React.lazy(() => import('../containers/Snapshot'))

const SiteSettings = React.lazy(() => import('../containers/settings/Site'))

const NotFound = React.lazy(() => import('../components/templates/NotFound'))

const NotAuthorised = React.lazy(() => import('../containers/NotAuthorised'))

const Site = () => {
  const { teamId, siteId } = useParams()
  const { setSite, site, can, error } = useSession()

  useEffect(() => {
    setSite({ teamId, siteId })
  }, [siteId])

  if (error?.type === 'Site')
    return (
      <Suspense fallback={<div />}>
        <NotFound id="site" />
      </Suspense>
    )

  if (site)
    return (
      <Routes>
        <Route path="/" element={<Navigate to="pulse" replace={true} />} />
        <Route
          end
          path="pulse"
          element={
            <LazyLoadingMainComponent>
              <Pulse />
            </LazyLoadingMainComponent>
          }
        />
        <Route path="crux" element={<Navigate to="dashboard" replace={true} />} />
        <Route
          end
          path="crux/dashboard"
          element={
            <LazyPageComponent>
              <CruxDashboard />
            </LazyPageComponent>
          }
        />
        <Route
          end
          path="crux/pages"
          element={
            <LazyPageComponent>
              <CruxPagesOverview />
            </LazyPageComponent>
          }
        />
        <Route
          end
          path="pages"
          element={
            <LazyPageComponent>
              <Pages />
            </LazyPageComponent>
          }
        />
        <Route
          end
          path="budgets"
          element={
            <LazyPageComponent>
              <BudgetsOverview />
            </LazyPageComponent>
          }
        />
        <Route
          end
          path="budgets/new"
          element={
            can('createMetricBudgets') ? (
              <LazyPageComponent>
                <NewBudget />
              </LazyPageComponent>
            ) : (
              <NotAuthorised />
            )
          }
        />
        <Route
          end
          path="budgets/:uuid"
          element={
            can('readSites') ? (
              <LazyLoadingMainComponent>
                <Budget />
              </LazyLoadingMainComponent>
            ) : (
              <NotAuthorised />
            )
          }
        />
        <Route
          end
          path="budgets/:uuid/edit"
          element={
            can('updateMetricBudgets') ? (
              <LazyPageComponent>
                <EditBudget />
              </LazyPageComponent>
            ) : (
              <NotAuthorised />
            )
          }
        />
        <Route
          end
          path="settings/*"
          element={
            can('updateSites') ? (
              <LazyLoadingMainComponent>
                <SiteSettings />
              </LazyLoadingMainComponent>
            ) : (
              <NotAuthorised />
            )
          }
        />
        <Route
          end
          path="metrics/:measurement/"
          element={
            can('readSites') ? (
              <LazyLoadingMainComponent>
                <Metric />
              </LazyLoadingMainComponent>
            ) : (
              <NotAuthorised />
            )
          }
        />
        <Route
          end
          path="snapshots"
          element={
            <LazyLoadingMainComponent>
              <Snapshots />
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="snapshots/:snapshotId/*"
          element={
            <LazyLoadingMainComponent>
              <Snapshot />
            </LazyLoadingMainComponent>
          }
        />
      </Routes>
    )

  return <LoadingRoute />
}

export default Site
